import { useMemo } from 'react';
import {
  type CreateExpenseInput,
  createExpenseSchema,
  type ExpenseCategoryRow,
  type ExpenseRow,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { FormActions } from '#client/components/ui/FormActions';
import { NumberField } from '#client/components/ui/NumberField';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { Select } from '#client/components/ui/Select';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { useFormValues, type SetField } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { useApiResource } from '#client/hooks/useResourceItem';
import { ENDPOINT } from '#client/lib/endpoints';
import { RESOURCE } from '#client/lib/routes';
import { dateKey } from '#client/lib/dates';
import { categoryOptions, initialExpenseValues } from './expense.logic';

const PATH = RESOURCE.expenses;

export function ExpenseFormPage() {
  return (
    <ResourceFormShell<ExpenseRow> path={PATH} noun="expense">
      {({ id, record }) => <ExpenseForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function ExpenseForm({ id, record }: { id?: string | undefined; record?: ExpenseRow | undefined }) {
  const categories = useApiResource<ExpenseCategoryRow[]>(ENDPOINT.expenseCategories);

  // Today, in the browser's calendar — `toISOString()` would convert to UTC
  // first and offer yesterday's date to anyone entering one before 5 a.m. PKT.
  const { values, set } = useFormValues<CreateExpenseInput>(
    initialExpenseValues(record, dateKey(new Date())),
  );

  const form = useResourceForm({
    path: PATH,
    id,
    schema: createExpenseSchema,
    noun: 'Expense',
  });

  const options = useMemo(() => categoryOptions(categories.data ?? []), [categories.data]);

  return (
    <form onSubmit={form.handleSubmit(values)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <Banner tone="info">
          Reagents and consumables are not entered here. Their cost is worked out from the stock
          ledger when a result is entered, and a category for them would count the same spend twice.
        </Banner>

        <Card>
          <ExpenseCostFields
            fields={{ values, onFieldChange: set, errors: form.errors }}
            categoryOptions={options}
          />
          <ExpenseDetailFields fields={{ values, onFieldChange: set, errors: form.errors }} />
        </Card>

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Record expense'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}

/** The form state a field group reads and writes. */
interface ExpenseFieldState {
  values: CreateExpenseInput;
  onFieldChange: SetField<CreateExpenseInput>;
  errors: Record<string, string>;
}

/** What the cost was, when, and how much. */
function ExpenseCostFields({
  fields: { values, onFieldChange, errors },
  categoryOptions: options,
}: {
  fields: ExpenseFieldState;
  categoryOptions: { value: string; label: string }[];
}) {
  return (
    <div className="c-form-grid">
      <Select
        label="Category"
        value={values.category_id === 0 ? '' : String(values.category_id)}
        error={errors.category_id}
        onChange={(choice) => onFieldChange('category_id', Number(choice))}
        options={options}
        placeholder="Select a category"
        required
      />
      <TextField
        label="Date incurred"
        type="date"
        value={values.incurred_on}
        error={errors.incurred_on}
        onChange={(event) => onFieldChange('incurred_on', event.target.value)}
        helpText="The day the cost arose, not the day it was entered"
        required
      />
      <NumberField
        label="Amount"
        min={0}
        step="0.01"
        value={values.amount}
        error={errors.amount}
        onChange={(amount) => onFieldChange('amount', amount)}
        // A refund is a different thing and would quietly flatter the
        // margin; the database has a CHECK saying the same.
        helpText="Rupees. Record a refund as its own reduced expense, not a negative one."
        required
      />
    </div>
  );
}

/** What it was for, who was paid, and the paperwork behind it. */
function ExpenseDetailFields({
  fields: { values, onFieldChange, errors },
}: {
  fields: ExpenseFieldState;
}) {
  return (
    <>
      <TextField
        label="Description"
        value={values.description}
        error={errors.description}
        onChange={(event) => onFieldChange('description', event.target.value)}
        helpText="What it was for, e.g. September salaries or generator diesel"
        required
      />

      <div className="c-form-grid">
        <TextField
          label="Paid to"
          value={values.vendor ?? ''}
          error={errors.vendor}
          onChange={(event) => onFieldChange('vendor', event.target.value)}
        />
        <TextField
          label="Reference"
          value={values.reference ?? ''}
          error={errors.reference}
          onChange={(event) => onFieldChange('reference', event.target.value)}
          helpText="Invoice or voucher number"
        />
      </div>

      <TextArea
        label="Note"
        value={values.note ?? ''}
        error={errors.note}
        onChange={(event) => onFieldChange('note', event.target.value)}
        rows={3}
      />
    </>
  );
}
